import { Helmet } from 'react-helmet-async';

const defaultSEO = {
  title: 'Afriqoma - Africa\'s #1 B2B Wholesale Marketplace | Import from 54 African Countries FREE',
  description: 'Import directly from Africa\'s top manufacturers. 40+ verified suppliers in Nigeria, Ghana, Kenya, South Africa. Buy cocoa, coffee, shea butter, gold, diamonds, textiles wholesale. 1-month FREE trial. No middlemen. Start sourcing today!',
  image: 'https://afriqoma.com/og-image.png',
  url: 'https://afriqoma.com',
  siteName: 'Afriqoma',
  author: 'Afriqoma Global Trade Hub'
};

export const SEO = ({ 
  title, 
  description, 
  image, 
  url,
  type = 'website',
  keywords,
  noindex = false,
  structuredData = null,
  article = null,
  breadcrumbs = null,
  product = null
}) => {
  // For canonical URL: use explicit url prop, or derive from current path, or fallback to homepage
  const canonicalUrl = url || (typeof window !== 'undefined' ? `https://afriqoma.com${window.location.pathname}` : defaultSEO.url);
  
  const seo = {
    title: title ? `${title} | Afriqoma` : defaultSEO.title,
    description: description || defaultSEO.description,
    image: image || defaultSEO.image,
    url: canonicalUrl
  };

  // Default organization structured data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Afriqoma",
    "alternateName": "Afriqoma Global Trade Hub",
    "url": "https://afriqoma.com",
    "logo": "https://afriqoma.com/logo.png",
    "description": "Africa's leading B2B marketplace connecting verified African manufacturers with global buyers across 54 countries",
    "foundingDate": "2024",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-770-376-5550",
      "contactType": "customer service",
      "email": "support@afriqoma.com",
      "availableLanguage": ["English", "French", "Arabic", "Portuguese"],
      "areaServed": ["Africa", "World"]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Delaware, Suite C-100",
      "addressLocality": "Delaware",
      "addressRegion": "OH",
      "postalCode": "44122",
      "addressCountry": "US"
    },
    "sameAs": []
  };

  // Generate breadcrumb structured data if provided
  const breadcrumbData = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": {
        "@type": "WebPage",
        "@id": `https://afriqoma.com${item.url}`,
        "name": item.name
      }
    }))
  } : null;

  // Generate product structured data if provided
  const productData = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images?.[0] || defaultSEO.image,
    "brand": {
      "@type": "Brand",
      "name": product.supplier_name || "African Supplier"
    },
    "offers": {
      "@type": "Offer",
      "url": `https://afriqoma.com/products/${product.id}`,
      "priceCurrency": product.currency || "USD",
      "price": product.price,
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": product.supplier_name || "African Supplier"
      }
    },
    "category": product.category,
    "countryOfOrigin": {
      "@type": "Country",
      "name": product.origin_country || "Africa"
    }
  } : null;

  // Article structured data for blog/content pages
  const articleData = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image || defaultSEO.image,
    "author": {
      "@type": "Organization",
      "name": "Afriqoma"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Afriqoma",
      "logo": {
        "@type": "ImageObject",
        "url": "https://afriqoma.com/logo.png"
      }
    },
    "datePublished": article.publishedDate,
    "dateModified": article.modifiedDate || article.publishedDate
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="author" content={defaultSEO.author} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* SEO Control */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Geographic & Language */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="3 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="coverage" content="Worldwide" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Afriqoma - B2B African Marketplace" />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@afriqoma" />
      <meta name="twitter:creator" content="@afriqoma" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content="Afriqoma - B2B African Marketplace" />
      
      {/* Canonical - only for indexable pages */}
      {!noindex && <link rel="canonical" href={seo.url} />}
      
      {/* Alternate Language Versions - only for indexable pages */}
      {!noindex && <link rel="alternate" hrefLang="en" href={seo.url} />}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={seo.url} />}
      
      {/* Structured Data - Organization (always include) */}
      {Array.isArray(structuredData) ? (
        structuredData.map((data, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(structuredData || organizationData)}
        </script>
      )}
      
      {/* Structured Data - Breadcrumbs */}
      {breadcrumbData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      )}
      
      {/* Structured Data - Product */}
      {productData && (
        <script type="application/ld+json">
          {JSON.stringify(productData)}
        </script>
      )}
      
      {/* Structured Data - Article */}
      {articleData && (
        <script type="application/ld+json">
          {JSON.stringify(articleData)}
        </script>
      )}
    </Helmet>
  );
};

// Page-specific SEO configurations with comprehensive keywords
export const pageSEO = {
  home: {
    title: null, // Uses default
    description: "Source products directly from African manufacturers. 40+ verified suppliers across 54 countries. Cocoa from Ghana, coffee from Ethiopia, gold from South Africa, shea butter from Nigeria. 1-month FREE trial. Only 0.2% commission. Join 1000+ global buyers.",
    keywords: 'african wholesale suppliers, buy from africa wholesale, import african products, african manufacturers directory, b2b africa marketplace, cocoa suppliers ghana direct, ethiopian coffee exporters, shea butter wholesale africa, gold suppliers africa verified, nigerian exporters database, kenyan tea suppliers, south african manufacturers, african commodities trading, alibaba africa alternative, african trade platform free, wholesale from africa to usa, african products importers, verified african suppliers 2025, african b2b platform, source from africa direct, african export companies, african business suppliers, bulk buy africa, african factory direct, trade with african countries'
  },
  products: {
    title: 'African Products Wholesale | 150+ Products Direct from Manufacturers',
    description: 'Wholesale African products: cocoa beans $2,800/MT, Ethiopian coffee, shea butter $1,200/MT, palm oil, gold bullion, diamonds, Ankara textiles. Direct factory prices. MOQ from 1 ton. Request free quotes from 40+ verified suppliers.',
    keywords: 'african products wholesale prices, cocoa beans price per ton, ethiopian coffee wholesale, shea butter bulk buy, palm oil suppliers africa, african gold bullion wholesale, diamonds africa direct, ankara fabric wholesale, african commodities prices 2025, cocoa suppliers ghana price, coffee ethiopia direct, nigerian palm oil export, african wholesale catalog, bulk african products, factory direct africa, moq african products'
  },
  suppliers: {
    title: 'Verified African Suppliers | 40+ Manufacturers - Dangote, Olam, OCP & More',
    description: 'Connect with Africa\'s top manufacturers: Dangote Industries Nigeria, Olam Ghana, AngloGold Ashanti, OCP Morocco, KTDA Kenya. Verified suppliers. Direct contact FREE. No agents. View storefronts, catalogs & certifications.',
    keywords: 'african suppliers list verified, dangote industries contact, olam nigeria supplier, anglogold ashanti sales, ocp group morocco, kenyan tea suppliers ktda, african manufacturers database, verified exporters africa, nigerian suppliers list, ghana cocoa suppliers verified, kenya flower exporters, south africa mining suppliers, african supplier directory 2025, trusted african vendors, certified african exporters'
  },
  categories: {
    title: 'African Product Categories | Agricultural, Minerals, Manufacturing & More',
    description: 'Explore 14 African product categories: Agricultural (cocoa, coffee), Minerals (gold, diamonds), Manufacturing, Oil & Gas, Textiles & Fashion. Source quality products from verified African suppliers.',
    keywords: 'African product categories, agricultural Africa, African minerals, manufacturing Africa, African textiles, oil gas Africa, African commodities, product sourcing Africa, African industries, import categories Africa'
  },
  register: {
    title: 'Join Afriqoma FREE | Contact African Suppliers Unlimited for 1 Year',
    description: 'Sign up FREE in 2 minutes. Buyers: 10 supplier contacts FREE, no credit card. Suppliers: List products FREE, reach global buyers, keep 99.8% revenue. 40+ verified suppliers. Start importing/exporting today!',
    keywords: 'afriqoma signup free, register african marketplace, free b2b africa account, become african supplier free, buyer registration africa, african trade platform signup, import from africa registration, export africa free listing, join african marketplace, african supplier registration, b2b africa free trial'
  },
  login: {
    title: 'Sign In | Access Your Afriqoma Account',
    description: 'Sign in to Afriqoma to manage your products, contact suppliers, track orders and grow your African trade business. Secure login with 2FA protection.',
    keywords: 'Afriqoma login, sign in Afriqoma, African marketplace login, Afriqoma account access, B2B Africa sign in'
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Manage your Afriqoma account, products, orders, and supplier contacts.',
    noindex: true
  },
  pricing: {
    title: 'Afriqoma Pricing | 1-Month FREE Trial + Only 0.2% Commission',
    description: 'Best B2B marketplace pricing: 1-month FREE trial on all plans! Starter FREE (10 contacts), Basic $29/mo (50 contacts), Professional $100/mo (200 contacts), Enterprise $150/mo (unlimited). Only 0.2% commission on sales.',
    keywords: 'afriqoma pricing plans, free b2b marketplace africa, african trade subscription, lowest commission marketplace, alibaba alternative cheaper, b2b africa free year, supplier subscription africa, african marketplace cost, wholesale platform pricing, 0.2 percent commission marketplace'
  },
  howItWorks: {
    title: 'How to Import from Africa | 4 Steps to Source Directly from Suppliers',
    description: 'Import from Africa in 4 steps: 1) Browse 150+ products 2) Contact verified suppliers FREE 3) Negotiate directly - no agents 4) Secure checkout with buyer protection. Average savings: 30-50% vs middlemen.',
    keywords: 'how to import from africa, african sourcing guide, buy from african suppliers how, import cocoa from ghana steps, source coffee ethiopia guide, african wholesale process, direct trade africa tutorial, african manufacturing sourcing, b2b africa step by step, buying from africa for beginners'
  },
  about: {
    title: 'About Afriqoma | Africa\'s Trusted B2B Trade Platform',
    description: 'Afriqoma connects verified African manufacturers with global buyers. Company Reg. 9555700. 40+ verified suppliers, 150+ products, 54 African countries. Our mission: Make African trade simple.',
    keywords: 'about Afriqoma, Afriqoma company, African trade platform, B2B marketplace Africa, who is Afriqoma, Afriqoma mission, African commerce platform'
  },
  becomeSupplier: {
    title: 'Sell on Afriqoma | African Suppliers: Reach 1000+ Global Buyers FREE',
    description: 'African manufacturers: List products FREE, reach buyers in USA, Europe, Asia. Keep 99.8% revenue (only 0.2% commission). Get verified badge, analytics dashboard, marketing support. Join Dangote, Olam, 40+ top suppliers.',
    keywords: 'sell on afriqoma, african supplier registration, export from africa platform, list products africa free, african manufacturer listing, sell to global buyers, african exporter signup, b2b selling africa, african vendor marketplace, export african products online, african factory listing, reach international buyers africa'
  },
  tracking: {
    title: 'Track Your Package | Afriqoma Shipment Tracking',
    description: 'Track your Afriqoma order shipment. Supports DHL, FedEx, UPS, Maersk, GIG Logistics, Sendy & 20+ carriers. Real-time tracking updates for international & African local delivery.',
    keywords: 'Afriqoma tracking, track African shipment, package tracking Africa, order tracking, DHL tracking, African courier tracking, shipment status'
  },
  affiliate: {
    title: 'Afriqoma Affiliate Program | Earn 5% Commission for 2 Years',
    description: 'Earn money promoting African trade! 5% commission for 2 years on every referral, then 0.2% lifetime recurring. $20 minimum payout via bank or M-Pesa/MTN Mobile Money. Free signup. Earn from $500-$5000/month.',
    keywords: 'afriqoma affiliate program, african b2b affiliate, earn commission africa trade, referral program africa, affiliate marketing african products, b2b affiliate high commission, africa trade partner program, mobile money affiliate payout, recurring commission affiliate, african marketplace referral'
  },
  guides: {
    title: 'Guides & Resources | Afriqoma Help Center',
    description: 'Afriqoma guides: Supplier registration, Google Business Profile setup, marketing materials, delivery confirmation. Everything you need to succeed on Afriqoma.',
    keywords: 'Afriqoma guides, help center, supplier guide, how to sell on Afriqoma, African trade resources, Afriqoma tutorials'
  }
};

// Category-specific SEO for dynamic pages
export const categorySEO = {
  agricultural: {
    title: 'Agricultural Products from Africa - Wholesale',
    description: 'Source premium African agricultural products: cocoa beans, coffee, shea butter, cashews, palm oil, rubber, tea, and more. Direct from verified African farms and cooperatives.',
    keywords: 'African agriculture, cocoa beans Ghana, coffee Ethiopia Kenya, shea butter wholesale, cashews Tanzania, palm oil Nigeria, rubber Africa, African farm products, agricultural exports Africa'
  },
  manufacturing: {
    title: 'African Manufacturing & Industrial Products',
    description: 'Connect with African manufacturers for cement, fertilizers, chemicals, textiles, and industrial goods. Quality manufacturing from Nigeria, South Africa, Egypt, Morocco.',
    keywords: 'African manufacturing, cement Africa, fertilizers Africa, industrial products, textiles manufacturing, chemicals Africa, Dangote cement, African factories'
  },
  'oil-gas': {
    title: 'Oil & Gas from Africa - Crude, LNG, Petroleum',
    description: 'Source crude oil, LNG, and petroleum products from major African producers: Nigeria, Angola, Libya, Algeria, Egypt. Bonny Light, Es Sider, and more grades available.',
    keywords: 'African oil, Nigerian crude oil, Bonny Light, LNG Africa, petroleum Africa, Angola crude, Libya oil, Algeria gas, NNPC, Sonangol, Sonatrach'
  },
  minerals: {
    title: 'African Minerals & Mining - Gold, Diamonds, Copper',
    description: 'Connect with verified African mining suppliers for gold, diamonds, copper, cobalt, manganese, platinum, tanzanite, and bauxite. Ethically sourced minerals.',
    keywords: 'African minerals, gold Ghana, diamonds Botswana Namibia, copper Zambia DRC, cobalt Congo, manganese South Africa, platinum, tanzanite Tanzania, bauxite Guinea, African mining'
  },
  services: {
    title: 'Business Services in Africa - Logistics & Professional',
    description: 'Find professional business services across Africa: logistics, consulting, legal, financial, IT services. Support your African trade operations.',
    keywords: 'African business services, logistics Africa, consulting Africa, professional services, trade support Africa'
  },
  electronics: {
    title: 'Electronics from Africa - Tech Products',
    description: 'Source electronics and technology products from African manufacturers and distributors. Growing tech manufacturing sector.',
    keywords: 'electronics Africa, technology Africa, tech products, African tech manufacturing'
  },
  textiles: {
    title: 'African Textiles & Fabrics - Ankara, Kente, Cotton',
    description: 'Discover authentic African textiles: Ankara fabric, Kente cloth, Adire, cotton, and traditional African fabrics. Wholesale and bulk orders available.',
    keywords: 'African textiles, Ankara fabric wholesale, Kente cloth, African fashion fabric, cotton Africa, Nigerian textiles, Ghanaian fabric, African clothing wholesale'
  },
  vehicles: {
    title: 'Vehicles & Automotive from Africa',
    description: 'Source vehicles, automotive parts, and transportation equipment from African dealers and manufacturers.',
    keywords: 'vehicles Africa, automotive Africa, cars Nigeria, trucks Africa, transportation equipment'
  },
  fashion: {
    title: 'African Fashion & Apparel Wholesale',
    description: 'Wholesale African fashion: traditional clothing, modern African designs, accessories. Authentic African apparel for global markets.',
    keywords: 'African fashion wholesale, African clothing, traditional African wear, Afrocentric fashion, African accessories, dashiki wholesale'
  }
};

export default SEO;
